import React from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';
import { messages as enMessages } from './translations/en';
import { messages as nlMessages } from './translations/nl';
import { useLanguage, SupportedLanguage } from './LanguageContext';

// Define a non-circular type for nested messages
interface NestedMessages {
  [key: string]: string | NestedMessages | readonly unknown[];
}

const flattenMessages = (nestedMessages: NestedMessages, prefix = ''): Record<string, string> =>
  Object.entries(nestedMessages).reduce<Record<string, string>>((acc, [key, value]) => {
    const prefixedKey = prefix ? `${prefix}.${key}` : key;
    
    // Handle arrays (like FAQ items)
    if (Array.isArray(value)) {
      return {
        ...acc,
        [prefixedKey]: JSON.stringify(value)
      };
    }
    
    return typeof value === 'object'
      ? { ...acc, ...flattenMessages(value as NestedMessages, prefixedKey) }
      : { ...acc, [prefixedKey]: value };
  }, {});

const messages = {
  en: flattenMessages(enMessages),
  nl: flattenMessages(nlMessages),
} as const;

interface IntlProviderProps {
  children: React.ReactNode;
  initialLanguage?: SupportedLanguage;
}

export const IntlProvider: React.FC<IntlProviderProps> = ({ 
  children,
  initialLanguage
}) => {
  const { language } = useLanguage();
  
  // Use the language from context, or initialLanguage for SSG
  const currentLanguage = language || initialLanguage || 'en';

  return (
    <ReactIntlProvider
      messages={messages[currentLanguage]}
      locale={currentLanguage}
      defaultLocale="en"
    >
      {children}
    </ReactIntlProvider>
  );
}; 